import React from 'react';
import { X, FileText, Users, Database, Cloud, Activity, CreditCard, Shield, AlertTriangle, FileEdit, BookOpen, Mail, CheckCircle } from 'lucide-react';
import './TermsModal.scss';

const TermsModal = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <div className="terms-modal" role="dialog" aria-modal="true" aria-label="Terms and Conditions">
      <div className="terms-modal__backdrop" onClick={onClose} />
      <div className="terms-modal__panel">
        <header className="terms-modal__header">
          <h2><FileText size={22} className="header-icon" /> MIVENT Terms and Conditions</h2>
          <button className="terms-modal__close" onClick={onClose} aria-label="Close terms window"><X size={18} /></button>
        </header>

        <div className="terms-modal__content">
          <p>
            By accessing or using this Event Management System Platform (MIVENT), you
            agree to be bound by these Terms and Conditions. If you do not agree, please
            do not use the Platform.
          </p>

          <h3><Activity size={18} className="section-icon" /> 1. Platform Overview</h3>
          <p>
            This Platform is a web-based event management system designed to help users
            manage events, services, team members, quotations, billing, projects,
            notes, checklists, policies, analytics/dashboard, announcements, and
            related business activities. The Platform is accessible only through an
            internet connection and does not function as a standalone mobile or
            desktop application. Limited offline creation (new records only) is
            supported where available and syncs when connectivity is restored.
          </p>
          <p>
            You act as the data controller for any third-party personal data you enter
            (e.g., client names, emails, phone numbers, venue addresses). You confirm
            you have the necessary consent to store and share that data through the
            Platform, including via quotations, invoices, assignments, and
            WhatsApp/Email shares.
          </p>

          <h3><Users size={18} className="section-icon" /> 2. User Eligibility</h3>
          <p>
            Users must be at least 18 years old and provide accurate and complete
            information during registration, including a verifiable email address.
            You are responsible for maintaining the confidentiality of your login
            credentials. Any activity performed using your account is your
            responsibility.
          </p>
          <p>
            Your account is limited to <strong>2 concurrent active sessions</strong>.
            Attempting to sign in on a third device will show your existing active sessions;
            you must sign out of one before the new login can proceed. You can review and
            remotely end any session at any time from Settings.
          </p>

          <h3><Database size={18} className="section-icon" /> 3. Account &amp; Data Responsibility</h3>
          <p>
            Each registered user has access only to their own data. The Platform does not
            provide any backup, undo, restore, or recovery facility. Once data is deleted
            (events, services, bills, policies, or any content), it is permanently
            removed and cannot be recovered under any circumstances. Users are solely
            responsible for managing and maintaining their data.
          </p>

          <h3><Cloud size={18} className="section-icon" /> 4. Data Storage, Offline Data &amp; AI Assistant</h3>
          <p>
            The Platform stores only the data required for functionality. Deleted data is
            permanently erased from the system once deletion is completed and, where
            applicable, synced.
          </p>
          <p>By using MIVENT, you acknowledge and accept that:</p>
          <ul>
            <li>The Platform <strong>does not guarantee protection</strong> against unauthorized access, data loss, or system failures.</li>
            <li>Data security is a shared responsibility between the user and the Platform.</li>
            <li>You should maintain independent copies of any critical business data.</li>
            <li><strong>Offline data handling:</strong> Offline mode supports only the creation of new records (e.g., adding events, quotations, services, or notes). You cannot edit, update, or delete existing records while offline. New records added offline are temporarily stored in your browser's IndexedDB and are automatically submitted to the server when your internet connection is restored. These pending records remain on your device until they sync successfully or your browser data is cleared.</li>
            <li><strong>AI Assistant:</strong> The AI Assistant is a Gold plan, read-only feature. It uses your account, your question, conversation history, AI preferences, and relevant platform data to generate answers. It does not create, modify, or delete platform data. AI conversations and preferences are stored with your account.</li>
            <li><strong>AI processing:</strong> MIVENT may use a third-party AI provider, including Google Gemini, to process AI Assistant queries. Do not submit information through the AI Assistant that you do not want processed by that provider.</li>
          </ul>

          <h3><Activity size={18} className="section-icon" /> 5. Location &amp; Maps</h3>
          <p>
            Event services may include a venue address and an optional map link. A
            precise GPS location (latitude/longitude) is captured <strong>only when
            you tap “Use my current location”</strong> and allow the browser
            permission prompt — the Platform never tracks your location in the
            background.
          </p>
          <ul>
            <li>Typed address searches are sent to OpenStreetMap providers (Photon / Nominatim) to show suggestions.</li>
            <li>GPS coordinates are sent to reverse-geocoding providers (BigDataCloud / Nominatim) to fill the place name.</li>
            <li>Opening a map link takes you to Google Maps, which applies its own terms and privacy policy.</li>
            <li>Saved locations are stored with the event service and may be shared with assigned team members and clients via notifications and WhatsApp/Email shares.</li>
          </ul>

          <h3><Activity size={18} className="section-icon" /> 6. Notifications, Push, Email &amp; Announcements</h3>
          <p>
            The Platform sends in-app notifications, optional browser push
            notifications, transactional emails (verification, password reset,
            quotations/invoices, payment receipts), and admin announcements.
          </p>
          <ul>
            <li><strong>Push notifications</strong> require your browser permission and can be disabled any time from browser settings or by unsubscribing. Push delivery is routed through your browser vendor’s push service (Google, Mozilla, or Apple).</li>
            <li><strong>Emails</strong> are transactional only and sent via our email provider (Gmail SMTP, migrating to a dedicated provider such as Resend/SES). Marketing emails are never sent without consent.</li>
            <li><strong>Announcements</strong> may be targeted by plan or role. Views and dismissals may be recorded to avoid repeat display.</li>
            <li><strong>WhatsApp/Email shares</strong> are user-initiated via wa.me / mailto links. Message content (client, service, amount, venue, map link) is composed in your browser and processed by WhatsApp or your email app under their policies — the Platform does not send WhatsApp messages server-side.</li>
          </ul>

          <h3><Activity size={18} className="section-icon" /> 7. Uploads &amp; Media</h3>
          <p>
            Profile photos, company logos, UPI QR images, and announcement media are
            hosted on Cloudinary. Uploaded file URLs may be publicly accessible to
            anyone with the link (e.g., logos embedded in shared PDF quotations).
            You confirm you own or have rights to upload the content, and that photos
            of identifiable people are uploaded with their consent. Replacing or
            deleting an image removes the reference from your account; cached or
            previously shared copies may persist.
          </p>

          <h3><Activity size={18} className="section-icon" /> 8. Platform Availability &amp; Maintenance</h3>
          <p>
            The Platform may experience downtime due to maintenance, updates, or technical
            issues. Continuous availability is not guaranteed. Scheduled maintenance
            and important changes may be announced via an in-app banner or
            announcement notification. The Platform owner is not
            liable for any losses caused due to downtime or service interruption.
          </p>

          <h3><CreditCard size={18} className="section-icon" /> 9. Subscription, Trial &amp; Pricing</h3>
          <p>
            New accounts receive a <strong>15-day free trial</strong> (no card required).
            Paid plans (Silver / Gold) are typically valid for one year from activation
            unless a limited offer states otherwise. Admins may grant extra days or
            limited-period signup offers. Trial expiry suspends paid features until a
            subscription is activated.
          </p>
          <p>
            MIVENT reserves the right to change subscription pricing, plan features, and plan structures at any time. Such changes will not affect active subscriptions until the end of their current subscription period. Revised pricing will apply only upon renewal, upgrade, downgrade, reactivation, or purchase of a new subscription after the effective date of the change. All subscription fees are payable in advance and are non-refundable except where required by law. Payments are processed by Razorpay — see Payment Terms for details.
          </p>

          <h3><Shield size={18} className="section-icon" /> 10. Acceptable Use</h3>
          <p>Users must not:</p>
          <ul>
            <li>Misuse the Platform</li>
            <li>Upload illegal, harmful, or abusive content, or content you have no rights to share</li>
            <li>Upload personal data of clients or team members without their consent</li>
            <li>Attempt to hack, exploit, scrape, or reverse-engineer the system, including location search endpoints</li>
            <li>Abuse sharing, notification, or feedback features for spam or harassment</li>
            <li>Use the Platform for unlawful activities</li>
          </ul>
          <p>Violation may result in suspension or permanent termination of access.</p>

          <h3><AlertTriangle size={18} className="section-icon" /> 11. Limitation of Liability</h3>
          <p>
            The Platform is provided on an “as-is” and “as-available” basis. The Platform
            owner shall not be liable for:
          </p>
          <ul>
            <li>Data loss</li>
            <li>Business loss</li>
            <li>Revenue loss</li>
            <li>System errors or platform outages</li>
            <li>User misuse</li>
            <li>Inaccurate map suggestions, GPS errors, or third-party provider failures (maps, push vendors, email delivery, Razorpay, Cloudinary, YouTube)</li>
          </ul>

          <h3><FileEdit size={18} className="section-icon" /> 12. Modifications to Terms</h3>
          <p>
            The Platform owner reserves the right to update or modify these Terms at any
            time. Material changes will be announced in-app and/or by email. Continued use of the Platform after the update indicates acceptance of the updated terms.
          </p>

          <h3><BookOpen size={18} className="section-icon" /> 13. Governing Law</h3>
          <p>These Terms shall be governed by and interpreted in accordance with the laws of India. Disputes are subject to the exclusive jurisdiction of the competent courts in India.</p>
        </div>

        <div className="terms-modal__contact" aria-label="Contact information">
          <h3><Mail size={18} className="contact-icon" /> Contact</h3>
          <p>If you have questions about these Terms, contact our support team:</p>
          <a href="mailto:miventsite@gmail.com" className="terms-modal__contact-link">
            <Mail size={16} />
            miventsite@gmail.com
          </a>
        </div>
        <footer className="terms-modal__footer">
          <button className="terms-modal__accept" onClick={onClose}>
            <CheckCircle size={18} />
            Close
          </button>
        </footer>
      </div>
    </div>
  );
};

export default TermsModal;
